import { API_BASE_URL } from '../config/constants';
import { ConfigFormData, ProcessingResponse, ChatRequest } from '../types/config';
import { DifyCitation, DifyRetrievalReference } from '../types/citations';

export interface FileUploadResponse {
  id: string;
  name: string;
  size: number;
  extension: string;
  mime_type: string;
  created_by: string;
  created_at: number;
}

export interface FileUploadRequest {
  type: string;
  transfer_method: 'local_file';
  upload_file_id: string;
}

export interface GitLabFileUploadResponse {
  success: boolean;
  data?: FileUploadResponse;
  sourceFile?: {
    gitlabUrl: string;
    filePath: string;
    fileName: string;
  };
  error?: string;
}

export interface EnhancedStreamingResponse {
  id?: string;
  conversation_id?: string;
  message_id?: string;
  task_id?: string;
  created_at?: number;
  // Enhanced with citations support
  metadata?: {
    retrieval_references?: DifyRetrievalReference[];
    citations?: DifyCitation[];
  };
}

export class EnhancedProxyService {
  static async processRequestWithCitations(
    config: any,
    formData: ConfigFormData,
    onMessage?: (data: string) => void,
    onError?: (error: string) => void,
    onComplete?: (responseData?: EnhancedStreamingResponse) => void,
    abortController?: AbortController | null
  ): Promise<ProcessingResponse> {
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/api/proxy/completion`;

      // Create the request payload
      const isChatApplication = config.requestUrl?.includes('chat-messages');
      const isWorkflowApplication = config.requestUrl?.includes('workflows/run');
      
      let requestPayload;
      if (isChatApplication) {
        requestPayload = formData;
      } else if (isWorkflowApplication) {
        const filteredInputs = Object.entries(formData).reduce((acc, [key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);
        
        requestPayload = {
          inputs: filteredInputs,
          response_mode: 'streaming',
          user: 'user-' + Date.now()
        };
      } else {
        requestPayload = {
          inputs: formData,
          response_mode: 'streaming',
          user: 'user-' + Date.now()
        };
      }
      
      const payload = {
        documentId: config.id,
        requestUrl: config.requestUrl,
        payload: requestPayload,
      };

      return new Promise((resolve, reject) => {
        this.streamRequestWithCitations(
          url, 
          payload, 
          token, 
          onMessage, 
          onError, 
          onComplete, 
          abortController
        ).then(resolve).catch(reject);
      });
    } catch (error: any) {
      throw error;
    }
  }

  private static async streamRequestWithCitations(
    url: string,
    payload: any,
    token: string | null,
    onMessage?: (data: string) => void,
    onError?: (error: string) => void,
    onComplete?: (responseData?: EnhancedStreamingResponse) => void,
    abortController?: AbortController | null
  ): Promise<ProcessingResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify(payload),
          signal: abortController?.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalResponse: EnhancedStreamingResponse = {};
        let accumulatedCitations: DifyCitation[] = [];
        let accumulatedReferences: DifyRetrievalReference[] = [];

        // Word-level buffering for markdown-safe streaming (like Dify)
        // Buffer content until we hit word boundaries to avoid sending partial markdown
        let contentBuffer = '';
        const flushContentBuffer = (force: boolean = false) => {
          if (!contentBuffer) return;

          if (force) {
            // Force flush all remaining content
            onMessage?.(contentBuffer);
            contentBuffer = '';
            return;
          }

          // Find the last safe boundary (space, newline, or certain punctuation)
          // This ensures markdown tokens like "##", "**", "```" aren't split
          const lastSafeIndex = Math.max(
            contentBuffer.lastIndexOf(' '),
            contentBuffer.lastIndexOf('\n'),
            contentBuffer.lastIndexOf('\t'),
          );

          if (lastSafeIndex > 0) {
            // Flush up to the safe boundary
            const toFlush = contentBuffer.substring(0, lastSafeIndex + 1);
            contentBuffer = contentBuffer.substring(lastSafeIndex + 1);
            onMessage?.(toFlush);
          } else if (contentBuffer.length > 200) {
            // If buffer is getting large without boundaries, flush it all
            // This handles edge cases like very long words or code
            onMessage?.(contentBuffer);
            contentBuffer = '';
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          // Process complete lines
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              if (data === '[DONE]') continue;
              
              if (data) {
                try {
                  const parsed = JSON.parse(data);

                  console.log('📨 Frontend received event:', parsed.event || 'unknown',
                    parsed.conversation_id ? `conversation_id: ${parsed.conversation_id}` : 'no conversation_id');

                  // Handle different Dify event types
                  if (parsed.event === 'message') {
                    // Regular message content - buffer for word-safe streaming
                    if (parsed.answer) {
                      contentBuffer += parsed.answer;
                      flushContentBuffer();
                    } else if (parsed.content) {
                      contentBuffer += parsed.content;
                      flushContentBuffer();
                    }

                    // Store basic response data including conversation_id
                    if (parsed.conversation_id) {
                      console.log('📥 Captured conversation_id from message event:', parsed.conversation_id);
                    }

                    finalResponse = {
                      ...finalResponse,
                      id: parsed.id,
                      conversation_id: parsed.conversation_id,
                      message_id: parsed.message_id,
                      task_id: parsed.task_id,
                      created_at: parsed.created_at,
                    };
                  }
                  
                  // Handle message_end event which may contain citations
                  if (parsed.event === 'message_end') {
                    // Capture conversation_id from message_end event
                    if (parsed.conversation_id) {
                      console.log('📥 Captured conversation_id from message_end event:', parsed.conversation_id);
                      finalResponse.conversation_id = parsed.conversation_id;
                    }

                    // Extract citations from message_end event (Dify uses retriever_resources)
                    // First try metadata.retriever_resources, then try top-level retriever_resources
                    const retrieverData = parsed.metadata?.retriever_resources || parsed.retriever_resources;
                    
                    if (retrieverData) {
                      // Handle both formats: direct array or {result: [...]} wrapper
                      const referencesArray = Array.isArray(retrieverData) ? retrieverData : retrieverData.result || [];
                      
                      accumulatedReferences = referencesArray.map((ref: any) => ({
                        content: ref.content || ref.title || '',
                        document_name: ref.document_name || ref.title || ref.metadata?.document_name || '',
                        score: parseFloat((ref.score || ref.metadata?.score || 0).toFixed(2)),
                        segment_id: ref.segment_id || ref.metadata?.segment_id || '',
                        document_id: ref.document_id || ref.metadata?.document_id,
                        dataset_id: ref.dataset_id || ref.metadata?.dataset_id,
                        hit_count: ref.hit_count || ref.metadata?.segment_hit_count,
                        position: ref.position || ref.metadata?.position,
                      }));
                    }
                    
                    if (parsed.citations) {
                      accumulatedCitations = parsed.citations.map((citation: any) => ({
                        id: citation.id || '',
                        content: citation.content || '',
                        document_name: citation.document_name || '',
                        score: parseFloat(citation.score?.toFixed(2) || '0'),
                        segment_id: citation.segment_id || '',
                        document_id: citation.document_id,
                        dataset_id: citation.dataset_id,
                        hit_count: citation.hit_count,
                      }));
                    }

                    // Update final response with metadata
                    finalResponse.metadata = {
                      retrieval_references: accumulatedReferences,
                      citations: accumulatedCitations,
                    };
                  }
                  // Handle agent thought events (for workflow apps)
                  else if (parsed.event === 'agent_thought') {
                    // May contain additional context or references
                    if (parsed.thought) {
                      // Could extract references from agent thoughts if needed
                    }
                  }
                  // Handle retrieval events specifically
                  else if (parsed.event === 'retrieval') {
                    if (parsed.retriever_resources) {
                      const newReferences = parsed.retriever_resources.map((ref: any) => ({
                        content: ref.content || '',
                        document_name: ref.document_name || ref.dataset_name || '',
                        score: parseFloat((ref.score || 0).toFixed(2)),
                        segment_id: ref.segment_id || '',
                        document_id: ref.document_id,
                        dataset_id: ref.dataset_id,
                        hit_count: ref.hit_count,
                        position: ref.position,
                      }));
                      accumulatedReferences.push(...newReferences);
                    }
                  }
                  // Handle node_finished event which contains knowledge retrieval results
                  else if (parsed.event === 'node_finished' && parsed.data?.node_type === 'knowledge-retrieval') {
                    
                    const retrievalResults = parsed.data?.outputs?.result;
                    if (Array.isArray(retrievalResults)) {
                      const newReferences = retrievalResults.map((ref: any) => ({
                        content: ref.content || ref.title || '',
                        document_name: ref.title || ref.metadata?.document_name || '',
                        score: parseFloat(ref.metadata?.score || 0),
                        segment_id: ref.metadata?.segment_id || '',
                        document_id: ref.metadata?.document_id || '',
                        dataset_id: ref.metadata?.dataset_id || '',
                        hit_count: ref.metadata?.segment_hit_count || 0,
                        position: ref.metadata?.position || 0,
                      }));
                      accumulatedReferences.push(...newReferences);
                    }
                  }
                  // Handle text_chunk events - buffer for word-safe streaming
                  else if (parsed.event === 'text_chunk' && parsed.data?.text) {
                    contentBuffer += parsed.data.text;
                    flushContentBuffer();
                  }
                  // Handle conversation start
                  else if (parsed.event === 'conversation_started') {
                    finalResponse = {
                      ...finalResponse,
                      conversation_id: parsed.data?.conversation_id,
                      task_id: parsed.data?.task_id,
                    };
                  }
                  // Handle workflow started/finished events
                  else if (parsed.event === 'workflow_started' || parsed.event === 'workflow_finished') {
                    if (parsed.data?.workflow_run_id) {
                      finalResponse.task_id = parsed.data.workflow_run_id;
                    }
                  }
                  // Handle error messages
                  else if (parsed.error) {
                    console.error('Enhanced Chat API Error:', parsed.error);
                    onError?.(`Error: ${parsed.error}`);
                  }
                } catch (e) {
                  console.error('Error parsing enhanced SSE data:', e, 'Raw line:', line);
                }
              }
            }
          }
        }

        // Flush any remaining buffered content at stream end
        flushContentBuffer(true);

        // Ensure final response has accumulated metadata
        if (!finalResponse.metadata && (accumulatedCitations.length > 0 || accumulatedReferences.length > 0)) {
          finalResponse.metadata = {
            citations: accumulatedCitations,
            retrieval_references: accumulatedReferences,
          };
        }

        // Call completion callback with enhanced response
        console.log('🎯 Final response being passed to completion callback:', {
          conversation_id: finalResponse.conversation_id,
          message_id: finalResponse.message_id,
          task_id: finalResponse.task_id
        });
        if (onComplete) {
          onComplete(finalResponse);
        }

        resolve({
          id: finalResponse.message_id || 'completed',
          status: 'completed',
          content: '', // Content was streamed via onMessage
          metadata: {
            tokensUsed: 0, // Default values to satisfy interface
            processingTime: 0,
            model: 'dify',
            ...finalResponse,
          } as any // Allow additional metadata properties
        });
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('Enhanced request aborted');
          resolve({
            id: 'aborted',
            status: 'completed',
            content: '',
          });
        } else {
          console.error('Enhanced streaming error:', error);
          reject(error);
        }
      }
    });
  }

  static async sendChatMessage(
    config: any,
    chatRequest: ChatRequest,
    onMessage?: (data: string) => void,
    onError?: (error: string) => void,
    onComplete?: (response: any) => void,
    abortController?: AbortController | null
  ): Promise<any> {
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/api/proxy/chat`;

      // Create the request payload for chat messages
      const payload = {
        documentId: config.id,
        requestUrl: config.requestUrl,
        payload: chatRequest,
      };

      return this.streamChatRequest(url, payload, token, onMessage, onError, onComplete, abortController);
    } catch (error: any) {
      console.error('Chat request failed:', error);
      throw new Error(error.message || 'Failed to send chat message');
    }
  }

  private static async streamChatRequest(
    url: string,
    payload: any,
    token: string | null,
    onMessage?: (data: string) => void,
    onError?: (error: string) => void,
    onComplete?: (response: any) => void,
    abortController?: AbortController | null
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify(payload),
          signal: abortController?.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalResponse: any = null;

        // Word-level buffering for markdown-safe streaming (like Dify)
        let contentBuffer = '';
        const flushContentBuffer = (force: boolean = false) => {
          if (!contentBuffer) return;
          if (force) {
            onMessage?.(contentBuffer);
            contentBuffer = '';
            return;
          }
          const lastSafeIndex = Math.max(
            contentBuffer.lastIndexOf(' '),
            contentBuffer.lastIndexOf('\n'),
            contentBuffer.lastIndexOf('\t'),
          );
          if (lastSafeIndex > 0) {
            const toFlush = contentBuffer.substring(0, lastSafeIndex + 1);
            contentBuffer = contentBuffer.substring(lastSafeIndex + 1);
            onMessage?.(toFlush);
          } else if (contentBuffer.length > 200) {
            onMessage?.(contentBuffer);
            contentBuffer = '';
          }
        };

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          // Process complete lines
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              console.log('Chat SSE data:', data);

              if (data === '[DONE]') {
                console.log('Chat stream completed');
                continue;
              }

              if (data) {
                try {
                  const parsed = JSON.parse(data);
                  console.log('Parsed chat SSE data:', parsed);

                  // Handle chat-specific events - buffer for word-safe streaming
                  if (parsed.event === 'message') {
                    if (parsed.answer) {
                      contentBuffer += parsed.answer;
                      flushContentBuffer();
                    } else if (parsed.content) {
                      contentBuffer += parsed.content;
                      flushContentBuffer();
                    }

                    // Store the final response for completion callback
                    finalResponse = {
                      id: parsed.id,
                      conversation_id: parsed.conversation_id,
                      message_id: parsed.message_id,
                      task_id: parsed.task_id,
                      created_at: parsed.created_at,
                    };
                  }
                  // Handle chatflow text_chunk events - buffer for word-safe streaming
                  else if (parsed.event === 'text_chunk' && parsed.data?.text) {
                    contentBuffer += parsed.data.text;
                    flushContentBuffer();
                  }
                  // Handle conversation start
                  else if (parsed.event === 'conversation_started') {
                    finalResponse = {
                      conversation_id: parsed.data?.conversation_id,
                      task_id: parsed.data?.task_id,
                    };
                  }
                  // Handle error messages
                  else if (parsed.error) {
                    console.error('Chat API Error:', parsed.error);
                    onError?.(`Error: ${parsed.error}`);
                  }
                } catch (e) {
                  console.error('Error parsing chat SSE data:', e, 'Raw line:', line);
                }
              }
            }
          }
        }

        // Flush any remaining buffered content
        flushContentBuffer(true);

        // Call completion callback with final response
        if (onComplete && finalResponse) {
          onComplete(finalResponse);
        }

        resolve(finalResponse);
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('Chat request aborted');
          resolve(null);
        } else {
          console.error('Chat stream error:', error);
          onError?.(error.message || 'Chat request failed');
          reject(error);
        }
      }
    });
  }

  static async uploadFileFromGitLab(
    gitlabUrl: string,
    filePath: string,
    uploadUrl: string,
    documentId: string
  ): Promise<GitLabFileUploadResponse> {
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/api/proxy/upload-from-gitlab`;

      const payload = {
        gitlabUrl,
        filePath,
        uploadUrl,
        documentId,
      };

      console.log('🚀 Uploading file from GitLab:', payload);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ GitLab file upload successful:', result);
      return result;
    } catch (error: any) {
      console.error('❌ GitLab file upload failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload file from GitLab'
      };
    }
  }

  static async uploadFile(file: File, uploadUrl: string, documentId: string): Promise<FileUploadResponse> {
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/api/proxy/upload`;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadUrl', uploadUrl);
      formData.append('documentId', documentId);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error: any) {
      console.error('File upload failed:', error);
      throw new Error(error.message || 'Failed to upload file');
    }
  }

  static async uploadFiles(files: File[], uploadUrl: string, documentId: string): Promise<FileUploadResponse[]> {
    if (files.length === 0) {
      return [];
    }

    const uploadPromises = files.map(file => this.uploadFile(file, uploadUrl, documentId));
    return Promise.all(uploadPromises);
  }

  static convertFilesToApiFormat(uploadedFiles: FileUploadResponse[], fileType: string = 'document'): any {
    if (uploadedFiles.length === 0) {
      return null;
    }
    
    // For single file, return object format
    if (uploadedFiles.length === 1) {
      return {
        type: fileType,
        transfer_method: 'local_file' as const,
        url: '',
        upload_file_id: uploadedFiles[0].id
      };
    }
    
    // For multiple files, return array format (fallback)
    return uploadedFiles.map(file => ({
      type: fileType,
      transfer_method: 'local_file' as const,
      url: '',
      upload_file_id: file.id
    }));
  }

  static getUploadUrl(baseUrl: string): string {
    // Remove trailing slashes
    const cleanUrl = baseUrl.replace(/\/+$/, '');
    
    // Construct the upload URL based on the Dify API format
    if (cleanUrl.endsWith('/v1')) {
      return `${cleanUrl}/files/upload`;
    } else {
      return `${cleanUrl}/v1/files/upload`;
    }
  }

  static getFileType(file: File, allowedTypes: string[] = []): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    // Map extensions to types
    const extensionToType: { [key: string]: string } = {
      // Documents
      'txt': 'document', 'md': 'document', 'markdown': 'document', 'pdf': 'document',
      'html': 'document', 'xlsx': 'document', 'xls': 'document', 'docx': 'document',
      'csv': 'document', 'eml': 'document', 'msg': 'document', 'pptx': 'document',
      'ppt': 'document', 'xml': 'document', 'epub': 'document',
      
      // Images
      'jpg': 'image', 'jpeg': 'image', 'png': 'image', 'gif': 'image',
      'webp': 'image', 'svg': 'image',
      
      // Audio
      'mp3': 'audio', 'm4a': 'audio', 'wav': 'audio', 'webm': 'audio', 'amr': 'audio',
      
      // Video
      'mp4': 'video', 'mov': 'video', 'mpeg': 'video', 'mpga': 'video'
    };

    const detectedType = extension ? extensionToType[extension] : 'document';
    
    // If allowed types are specified, use the first matching type
    if (allowedTypes.length > 0) {
      for (const allowedType of allowedTypes) {
        if (allowedType.toLowerCase() === detectedType) {
          return allowedType;
        }
      }
      // If no match found, return the first allowed type
      return allowedTypes[0];
    }
    
    return detectedType;
  }

  static async fetchParameters(parametersUrl: string, apiKey: string, documentId: string): Promise<any[]> {
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/api/proxy/parameters`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          parametersUrl,
          apiKey,
          documentId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch parameters: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle different response structures
      if (Array.isArray(result)) {
        // Direct array response
        return result;
      } else if (result.parameters && Array.isArray(result.parameters)) {
        // Response with parameters property
        return result.parameters;
      } else if (result.user_input_form && Array.isArray(result.user_input_form)) {
        // Response with user_input_form property (Dify-style)
        // Flatten: [{"text-input": {...}}, {"paragraph": {...}}] => [{type: "text-input", ...}, {type: "paragraph", ...}]
        return result.user_input_form.map((item: any) => {
          const type = Object.keys(item)[0];
          return { type, ...item[type] };
        });
      } else {
        // Fallback to empty array
        console.warn('No valid parameters found in response:', result);
        return [];
      }
    } catch (error: any) {
      console.error('Failed to fetch parameters:', error);
      throw new Error(error.message || 'Failed to fetch parameters');
    }
  }
}